import Vue from 'vue'
import Vuex from 'vuex'
import {
  authAPI,
  adminAccountsAPI,
  schoolsAPI,
  countriesAPI,
  usersAPI,
} from '../services/api'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    user: null,
    realm: localStorage.getItem('realm') || null,
    needToModifyPassword: localStorage.getItem('needToModifyPassword') === 'true',
    adminAccounts: [],
    schools: [],
    currentSchool: null,
    favorites: [],
    mySchools: [],
    schoolServices: [],
    schoolFilters: {
      search: '',
      subject: '',
      formation: '',
    },
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      if (user && user.organization) {
        const orgId = user.organization._id || user.organization
        localStorage.setItem('memberOrganizationId', typeof orgId === 'string' ? orgId : orgId.toString())
      } else {
        localStorage.removeItem('memberOrganizationId')
      }
    },
    SET_REALM(state, realm) {
      state.realm = realm
      if (realm) localStorage.setItem('realm', realm)
      else localStorage.removeItem('realm')
      if (realm !== 'member') localStorage.removeItem('memberOrganizationId')
    },
    SET_NEED_TO_MODIFY_PASSWORD(state, value) {
      state.needToModifyPassword = value
      localStorage.setItem('needToModifyPassword', value ? 'true' : 'false')
    },
    SET_ADMIN_ACCOUNTS(state, accounts) {
      state.adminAccounts = accounts
    },
    ADD_ADMIN_ACCOUNT(state, account) {
      state.adminAccounts.push(account)
    },
    UPDATE_ADMIN_ACCOUNT(state, account) {
      const index = state.adminAccounts.findIndex(a => a._id === account._id || a.id === account.id || a.id === account._id)
      if (index !== -1) {
        Vue.set(state.adminAccounts, index, account)
      }
    },
    DELETE_ADMIN_ACCOUNT(state, id) {
      state.adminAccounts = state.adminAccounts.filter(a => (a._id || a.id) !== id)
    },
    SET_SCHOOLS(state, schools) {
      state.schools = schools
    },
    SET_CURRENT_SCHOOL(state, school) {
      state.currentSchool = school
    },
    SET_FAVORITES(state, favorites) {
      state.favorites = favorites
    },
    ADD_FAVORITE(state, schoolId) {
      const school = state.schools.find(s => s._id === schoolId)
      if (school) {
        school.isFavorite = true
        if (!state.favorites.find(f => f._id === schoolId)) {
          state.favorites.push(school)
        }
      }
      if (state.currentSchool && state.currentSchool._id === schoolId) {
        state.currentSchool.isFavorite = true
      }
    },
    REMOVE_FAVORITE(state, schoolId) {
      const school = state.schools.find(s => s._id === schoolId)
      if (school) {
        school.isFavorite = false
      }
      state.favorites = state.favorites.filter(f => f._id !== schoolId)
      if (state.currentSchool && state.currentSchool._id === schoolId) {
        state.currentSchool.isFavorite = false
      }
    },
    SET_SCHOOL_FILTERS(state, filters) {
      state.schoolFilters = { ...state.schoolFilters, ...filters }
    },
    SET_MY_SCHOOLS(state, schools) {
      state.mySchools = schools
    },
    SET_SCHOOL_SERVICES(state, services) {
      state.schoolServices = services
    },
    ADD_MY_SCHOOL(state, school) {
      if (!state.mySchools.find(s => (s._id || s.id) === (school._id || school.id))) {
        state.mySchools.push(school)
      }
    },
  },
  actions: {
    async userRequestOtp({ commit }, { phone, register = false } = {}) {
      const response = await authAPI.userRequestOtp(phone, { register })
      return response.data
    },
    async userVerifyOtp({ commit }, { phone, otp, firstname, lastname, userType, staffFunction, currentSchoolName } = {}) {
      const response = await authAPI.userVerifyOtp(phone, otp, {
        firstname,
        lastname,
        userType,
        staffFunction,
        currentSchoolName,
      })
      const { token, user } = response.data
      if (token) localStorage.setItem('authToken', token)
      commit('SET_USER', user || { phone })
      localStorage.setItem('isAuthenticated', 'true')
      return response.data
    },
    async adminLogin({ commit }, credentials) {
      try {
        const response = await authAPI.adminLogin(credentials)
        const { token, account } = response.data
        if (token) {
          localStorage.setItem('authToken', token)
        }
        commit('SET_USER', account)
        commit('SET_REALM', 'admin')
        localStorage.setItem('isAuthenticated', 'true')
        return response.data
      } catch (error) {
        throw error
      }
    },
    async memberLogin({ commit }, credentials) {
      try {
        const response = await authAPI.memberLogin(credentials)
        const { token, member } = response.data
        if (token) {
          localStorage.setItem('authToken', token)
        }
        commit('SET_USER', member)
        commit('SET_REALM', 'member')
        localStorage.setItem('isAuthenticated', 'true')
        return response.data
      } catch (error) {
        throw error
      }
    },
    async partnerLogin({ commit }, credentials) {
      try {
        const response = await authAPI.partnerLogin(credentials)
        const { token, partner } = response.data
        if (token) {
          localStorage.setItem('authToken', token)
        }
        commit('SET_USER', partner)
        commit('SET_REALM', 'partner')
        localStorage.setItem('isAuthenticated', 'true')
        return response.data
      } catch (error) {
        throw error
      }
    },
    logout({ commit }) {
      commit('SET_USER', null)
      commit('SET_REALM', null)
      commit('SET_NEED_TO_MODIFY_PASSWORD', false)
      localStorage.removeItem('authToken')
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('realm')
      localStorage.removeItem('memberOrganizationId')
      localStorage.removeItem('needToModifyPassword')
    },
    // Admin Accounts
    async fetchAdminAccounts({ commit }, search) {
      try {
        const response = await adminAccountsAPI.getAll(search)
        commit('SET_ADMIN_ACCOUNTS', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching admin accounts:', error)
        throw error
      }
    },
    async createAdminAccount({ commit }, account) {
      try {
        const response = await adminAccountsAPI.create(account)
        commit('ADD_ADMIN_ACCOUNT', response.data)
        return response.data
      } catch (error) {
        throw error
      }
    },
    async updateAdminAccount({ commit }, account) {
      try {
        const id = account._id || account.id
        const response = await adminAccountsAPI.update(id, account)
        commit('UPDATE_ADMIN_ACCOUNT', response.data)
        return response.data
      } catch (error) {
        throw error
      }
    },
    async deleteAdminAccount({ commit }, id) {
      try {
        await adminAccountsAPI.delete(id)
        commit('DELETE_ADMIN_ACCOUNT', id)
      } catch (error) {
        throw error
      }
    },
    // Schools
    async fetchSchools({ commit }, params = {}) {
      try {
        const response = await schoolsAPI.getAll(params)
        commit('SET_SCHOOLS', response.data.schools || response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching schools:', error)
        throw error
      }
    },
    async fetchCountries({ commit }, params = {}) {
      try {
        const response = await countriesAPI.getAll(params)
        return response.data || []
      } catch (error) {
        console.error('Error fetching countries:', error)
        throw error
      }
    },
    async fetchSchoolBySlug({ commit }, slug) {
      try {
        const response = await schoolsAPI.getBySlug(slug)
        commit('SET_CURRENT_SCHOOL', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching school:', error)
        throw error
      }
    },
    async fetchFavorites({ commit }) {
      try {
        const response = await schoolsAPI.getFavorites()
        commit('SET_FAVORITES', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching favorites:', error)
        throw error
      }
    },
    async addFavorite({ commit }, schoolId) {
      try {
        await schoolsAPI.addFavorite(schoolId)
        commit('ADD_FAVORITE', schoolId)
      } catch (error) {
        console.error('Error adding favorite:', error)
        throw error
      }
    },
    async removeFavorite({ commit }, schoolId) {
      try {
        await schoolsAPI.removeFavorite(schoolId)
        commit('REMOVE_FAVORITE', schoolId)
      } catch (error) {
        console.error('Error removing favorite:', error)
        throw error
      }
    },
    setSchoolFilters({ commit }, filters) {
      commit('SET_SCHOOL_FILTERS', filters)
    },
    async fetchMySchools({ commit }) {
      try {
        const response = await usersAPI.getMySchools()
        const schools = response.data.schools || response.data || []
        commit('SET_MY_SCHOOLS', schools)
        return schools
      } catch (error) {
        console.error('Error fetching my schools:', error)
        throw error
      }
    },
    async addSchoolByCode({ commit }, shortCode) {
      const response = await usersAPI.addSchoolByCode(shortCode)
      const schools = response.data.schools || []
      commit('SET_MY_SCHOOLS', schools)
      return response.data
    },
    async createMySchool({ commit }, data) {
      const response = await usersAPI.createMySchool(data)
      const schools = response.data.schools || []
      commit('SET_MY_SCHOOLS', schools)
      return response.data
    },
    async updateMySchool({ commit }, { schoolId, data }) {
      const response = await usersAPI.updateMySchool(schoolId, data)
      const schools = response.data.schools || []
      commit('SET_MY_SCHOOLS', schools)
      return response.data
    },
    async fetchSchoolServices({ commit }, schoolId) {
      try {
        const response = await usersAPI.getSchoolServices(schoolId)
        commit('SET_SCHOOL_SERVICES', response.data || [])
        return response.data
      } catch (error) {
        console.error('Error fetching school services:', error)
        throw error
      }
    },
  },
  modules: {
  }
})
